import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import { getActivityLog } from "apiHandler/actions";
import { useUserData } from "components/customHooks/userDataContext";

import { PageContainer } from "./styles";
import SummaryCards from "./components/summaryCards";
import ActivityLogFilters from "./components/activityLogFilters";
import EmptyState from "./components/emptyState";
import ActivityLogsGrid from "./components/activityLogsGrid";
import ActivityLogsSkeleton from "./components/activityLogsSkeleton";
import LogDetailsDialogContent from "./components/logDetailsDialogContent";
import {
  buildUniqueUsers,
  downloadLogsCsv,
  formatDateForApi,
  getActivitySummary,
} from "./utils";
import { RefreshRounded } from "@mui/icons-material";

let lastInitialFetchKey = "";

const ActivityLogs = () => {
  const userData = useUserData();
  const token = userData?.access_token;
  const practiceId = userData?.selected_practice;

  const [logs, setLogs] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterLoading, setFilterLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedLog, setSelectedLog] = useState(null);

  const [filters, setFilters] = useState({
    user_id: "",
    start_date: "",
    end_date: "",
  });

  const fetchLogs = useCallback(
    async ({
      showMainLoader = false,
      overridePayload,
      shouldUpdateUsers = false,
    } = {}) => {
      if (!token || !practiceId) return;

      try {
        setError("");

        if (showMainLoader) {
          setLoading(true);
        } else {
          setFilterLoading(true);
        }
        // setLogs([]);
        const payload = overridePayload || {
          practice_id: practiceId,
          ...(filters.user_id ? { user_id: filters.user_id } : {}),
          ...(filters.start_date
            ? { start_date: formatDateForApi(filters.start_date) }
            : {}),
          ...(filters.end_date
            ? { end_date: formatDateForApi(filters.end_date) }
            : {}),
        };

        const res = await getActivityLog(payload, token);

        const responseData = Array.isArray(res?.data)
          ? res.data
          : Array.isArray(res)
            ? res
            : [];

        setLogs(responseData);

        if (shouldUpdateUsers || allUsers.length === 0) {
          setAllUsers(buildUniqueUsers(responseData));
        }
      } catch (err) {
        console.log("error: ", err);
        setError("Unable to load activity logs.");
        setLogs([]);
      } finally {
        setLoading(false);
        setFilterLoading(false);
      }
    },
    [
      token,
      practiceId,
      filters.user_id,
      filters.start_date,
      filters.end_date,
      allUsers.length,
    ],
  );

  useEffect(() => {
    if (!token || !practiceId) return;

    const currentKey = `${practiceId}-${token}`;
    if (lastInitialFetchKey === currentKey) return;
    lastInitialFetchKey = currentKey;

    fetchLogs({
      showMainLoader: true,
      shouldUpdateUsers: true,
      overridePayload: { practice_id: practiceId },
    });
  }, [token, practiceId, fetchLogs]);

  const summary = useMemo(() => getActivitySummary(logs), [logs]);

  const handleApplyFilters = () => {
    fetchLogs();
  };

  const handleClearFilters = async () => {
    const clearedFilters = {
      user_id: "",
      start_date: "",
      end_date: "",
    };

    setFilters(clearedFilters);

    await fetchLogs({
      overridePayload: {
        practice_id: practiceId,
      },
      shouldUpdateUsers: allUsers.length === 0,
    });
  };

  const handleExportCsv = () => {
    downloadLogsCsv(logs);
  };

  return (
    <PageContainer>
      <Box
        display={"flex"}
        alignItems={"center"}
        justifyContent={"space-between"}
      >
        <Box>
          <Typography variant="h4" fontWeight={800} mb={2}>
            Activity Logs
          </Typography>

          <Typography variant="body1" color="text.secondary" mb={3}>
            Track user activity across the mobile app by provider, date, and
            action type.
          </Typography>
        </Box>
        <Tooltip title="Refresh summary">
          <span>
            <IconButton
              onClick={fetchLogs}
              disabled={loading}
              sx={{
                width: 42,
                height: 42,
                border: "1px solid",
                borderColor: "divider",
                borderRadius: "12px",
                backgroundColor: "background.paper",
                boxShadow: "0 2px 10px rgba(0,0,0,0.06)",
                transition: "all 0.2s ease",
                "&:hover": {
                  backgroundColor: "grey.100",
                  transform: "rotate(90deg)",
                },
              }}
            >
              {loading ? (
                <CircularProgress size={20} thickness={5} />
              ) : (
                <RefreshRounded />
              )}
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      <SummaryCards summary={summary} />

      <Box mt={3}>
        <ActivityLogFilters
          filters={filters}
          setFilters={setFilters}
          allUsers={allUsers}
          onApply={handleApplyFilters}
          onClear={handleClearFilters}
          onExportCsv={handleExportCsv}
          loading={loading || filterLoading}
        />
      </Box>

      {error ? (
        <Alert severity="error" sx={{ mt: 3 }}>
          {error}
        </Alert>
      ) : null}

      {loading ? (
        <Box mt={3}>
          <ActivityLogsSkeleton />
        </Box>
      ) : logs.length === 0 ? (
        <Box mt={3}>
          <EmptyState
            title="No activity logs found"
            subtitle="Try changing the provider or date filters."
          />
        </Box>
      ) : (
        <Box mt={3}>
          <ActivityLogsGrid
            rows={logs}
            loading={filterLoading}
            onRowClick={(row) => setSelectedLog(row)}
          />
        </Box>
      )}

      <Dialog
        open={!!selectedLog}
        onClose={() => setSelectedLog(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Activity Details</DialogTitle>
        <DialogContent dividers>
          <LogDetailsDialogContent log={selectedLog} />
        </DialogContent>
      </Dialog>
    </PageContainer>
  );
};

export default ActivityLogs;
